/**
 * Course settings · "Recomandări": the referral offer (13.10.2026).
 *
 * Everybody who pays for their own place at the accredited course, in a cohort
 * on the site, gets a code; here the owner writes what the offer is. Each code
 * keeps the offer of the day it was made, so nothing set here changes what
 * somebody was already promised.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Loader2, Mail, Save, Send, ToggleLeft, ToggleRight } from "lucide-react";
import { api, apiErrorMessage } from "../../../lib/api";
import { sendQuestion, sentLine, waitingLine } from "../../../lib/referralAdmin";

const TEXT_MAX = 300;

export default function ReferralSettings({ data, save, canMutate, busy }) {
  const locked = !canMutate || busy !== null;
  const enabled = !!data.referral_enabled;
  const referredOn = !!data.referral_referred_enabled;
  const hasBenefit = !!(data.referral_referrer_benefit || "").trim();
  // Why the main switch cannot be turned on right now, or "".
  const blocked = !enabled && !hasBenefit ? "Scrie întâi ce primește cel care recomandă." : "";

  return (
    <div className="grid grid-cols-1 gap-4" data-testid="settings-tab-recomandari">
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5" data-testid="settings-card-referral">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-[#965EA7] mb-3">
          <Gift size={16} /> Recomandări
        </div>

        <Switch
          on={enabled}
          title={enabled ? "Recomandările sunt pornite" : "Recomandările sunt oprite"}
          hint={
            "Cine își plătește singur locul la cursul acreditat, într-o grupă de pe site, primește un cod în emailul de "
            + "confirmare a plății. Colegii lui îl trec la înscriere. Cât e oprit, nu se creează coduri și câmpul nu apare în checkout."
          }
          note={blocked || (enabled && !hasBenefit
            ? "Comutatorul e pornit, dar lipsește textul ofertei: nu rulează nimic până nu scrii ce primește cel care recomandă."
            : "")}
          disabled={locked || Boolean(blocked)}
          onToggle={() => save({ referral_enabled: !enabled }, "Comutatorul recomandărilor")}
          testid="settings-referral-enabled-toggle"
        />

        <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
          <TextSetting
            label="Ce primește cel care recomandă"
            hint="Apare în email exact cum îl scrii aici. Obligatoriu ca oferta să poată fi pornită."
            placeholder="ex. un voucher de 200 de lei pentru următorul curs"
            stored={data.referral_referrer_benefit}
            onSave={(v) => save({ referral_referrer_benefit: v }, "Beneficiul celui care recomandă")}
            locked={locked}
            testid="referral-referrer-benefit"
          />

          <div>
            <Switch
              compact
              on={referredOn}
              title="Și cel recomandat primește ceva"
              hint="Dacă e pornit, textul de mai jos apare în email și în checkout, când codul e valid. Beneficiul îl dă echipa."
              note={!referredOn && !(data.referral_referred_benefit || "").trim() ? "Scrie întâi textul, apoi pornește." : ""}
              disabled={locked || (!referredOn && !(data.referral_referred_benefit || "").trim())}
              onToggle={() => save({ referral_referred_enabled: !referredOn }, "Beneficiul celui recomandat")}
              testid="settings-referral-referred-toggle"
            />
            <TextSetting
              label="Ce primește cel recomandat"
              placeholder="ex. un ghid de prompturi pentru profesori"
              stored={data.referral_referred_benefit}
              onSave={(v) => save({ referral_referred_benefit: v }, "Textul pentru cel recomandat")}
              locked={locked}
              testid="referral-referred-benefit"
            />
          </div>

          <NumberSetting
            label="Câte persoane recomandate"
            hint="Un singur beneficiu, când atâtea persoane recomandate au plătit. Recomandările în plus se văd, dar nu aduc altul."
            min={1} max={20}
            stored={data.referral_required_referrals}
            onSave={(v) => save({ referral_required_referrals: v }, "Numărul de persoane")}
            locked={locked}
            testid="referral-required"
          />
          <NumberSetting
            label="Valabilitatea codului (zile)"
            hint="Cât timp poate fi folosit un cod, din ziua în care e trimis. Contează ziua în care colegul îl trece la înscriere, nu ziua plății."
            min={1} max={730}
            stored={data.referral_expires_days}
            onSave={(v) => save({ referral_expires_days: v }, "Termenul")}
            locked={locked}
            testid="referral-expires-days"
          />
        </div>

        <div className="mt-5 grid grid-cols-1 gap-3 lg:grid-cols-2">
          <Switch
            compact
            on={!!data.referral_portal_enabled}
            title="Arată recomandările în portalul cursanților"
            hint="Cine are cod vede în portal un card cu codul lui, linkul, câte recomandări a strâns și numele colegilor care și-au plătit locul."
            disabled={locked}
            onToggle={() => save({ referral_portal_enabled: !data.referral_portal_enabled }, "Cardul din portal")}
            testid="settings-referral-portal-toggle"
          />
          <Switch
            compact
            on={!!data.referral_notify_referrer}
            title="Anunță pe email cel care recomandă"
            hint="Un email la fiecare coleg care se înscrie cu codul lui și își plătește locul, și unul când își atinge numărul."
            disabled={locked}
            onToggle={() => save({ referral_notify_referrer: !data.referral_notify_referrer }, "Emailurile către cel care recomandă")}
            testid="settings-referral-notify-referrer-toggle"
          />
          <Switch
            compact
            on={!!data.referral_notify_admin_email}
            title="Anunță echipa pe email când cineva își atinge numărul de recomandări"
            hint={`Un email, o singură dată pentru fiecare cod, cu cine a recomandat, ce i s-a promis și cine s-a înscris.${
              data.referral_team_email ? ` Pleacă la ${data.referral_team_email}.` : ""}`}
            disabled={locked}
            onToggle={() => save({ referral_notify_admin_email: !data.referral_notify_admin_email }, "Emailul către echipă")}
            testid="settings-referral-notify-team-toggle"
          />
        </div>

        <p className="mt-5 border-t border-[#E5E7EB] pt-3 text-xs text-[#6B7280]" data-testid="referral-kept-promise">
          Ce a fost promis rămâne promis: fiecare cod păstrează oferta din ziua în care a fost trimis. Ce schimbi aici
          se aplică doar codurilor trimise de acum înainte.
        </p>
      </div>

      <SendToEnrolled canMutate={canMutate} live={enabled && hasBenefit} />
      <TestSends canMutate={canMutate} />
    </div>
  );
}

/**
 * "Trimite codul celor deja înscriși": whoever paid before the offer began
 * has no code. One press sends each of them the email with theirs; the days
 * of a code run from the day it is sent.
 */
function SendToEnrolled({ canMutate, live }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/referrals/waiting");
      setState(r.data);
    } catch (err) {
      setState({ error: apiErrorMessage(err, "Nu am putut citi câți cursanți așteaptă codul.") });
    }
  }, []);

  useEffect(() => { load(); }, [load, live]);

  const count = state?.count || 0;
  const send = async () => {
    if (!window.confirm(sendQuestion(count, state.expires_days, state.batch))) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/referrals/send-waiting");
      const line = sentLine(r.data);
      setLast(line);
      (line.tone === "ok" ? toast.success : toast.warning)(line.text);
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Codurile nu au putut fi trimise."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5" data-testid="settings-card-referral-send">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-[#965EA7] mb-3">
        <Send size={16} /> Cei deja înscriși
      </div>
      <p className="max-w-2xl text-xs text-[#6B7280]">
        Cine a plătit înainte să pornești recomandările nu are cod. De aici le trimiți un email cu al lor.
        Zilele de valabilitate curg din ziua în care îl trimiți.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={send}
          disabled={!canMutate || busy || !live || !count}
          className="inline-flex items-center gap-2 rounded-full bg-[#965EA7] px-4 py-2 text-sm font-semibold text-white hover:bg-[#7A4986] disabled:opacity-50"
          data-testid="referral-send-waiting"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          Trimite codul celor deja înscriși
        </button>
        <span className="text-sm text-[#373A45]" data-testid="referral-waiting-line">
          {state === null ? "Se numără…" : state.error || waitingLine(count)}
        </span>
      </div>
      {!live && count > 0 && (
        <p className="mt-2 text-xs font-semibold text-[#8a6a1a]" data-testid="referral-send-needs-offer">
          Pornește întâi recomandările: codurile se trimit doar cât oferta e pornită.
        </p>
      )}
      {last && (
        <p className={`mt-2 text-xs ${last.tone === "ok" ? "text-[#2F6B4E]" : "text-[#8a6a1a]"}`} data-testid="referral-sent-line">
          {last.text}
        </p>
      )}
    </div>
  );
}

/**
 * The two emails that carry a code, sent to the admin with a made-up code
 * and the offer as it is written above — to read them before starting.
 */
function TestSends({ canMutate }) {
  const [me, setMe] = useState("");
  const [sending, setSending] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data?.email || "")).catch(() => setMe(""));
  }, []);

  const send = async (key, body) => {
    const to = me || window.prompt("Trimite proba către (email):");
    if (!to) return;
    setSending(key);
    try {
      await api.post("/admin/emails/test-template", { ...body, to });
      toast.success(`Proba a plecat către ${to}.`);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Proba nu a putut fi trimisă."));
    } finally {
      setSending(null);
    }
  };

  const TESTS = [
    { key: "payment", label: "Confirmarea plății, cu codul", body: { template_kind: "payment_confirmed", with_referral: true } },
    { key: "code", label: "Emailul separat cu codul", body: { template_kind: "referral_code" } },
  ];

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5" data-testid="settings-card-referral-test">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-[#965EA7] mb-3">
        <Mail size={16} /> Probă
      </div>
      <p className="max-w-2xl text-xs text-[#6B7280]">
        Primești emailurile așa cum le-ar primi un cursant, cu un cod inventat (ABCD2345) și cu textele salvate mai sus.
        {me ? ` Pleacă la ${me}.` : ""} Nu se creează niciun cod.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {TESTS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => send(t.key, t.body)}
            disabled={!canMutate || sending !== null}
            className="inline-flex items-center gap-2 rounded-full border border-[#965EA7] px-4 py-2 text-sm font-semibold text-[#965EA7] hover:bg-[#F0E7F4] disabled:opacity-50"
            data-testid={`referral-test-${t.key}`}
          >
            {sending === t.key ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Switch({ on, title, hint, note, disabled, onToggle, testid, compact = false }) {
  return (
    <div className={`flex items-start justify-between gap-3 rounded-xl border border-[#965EA7]/20 bg-[#F0E7F4]/40 ${compact ? "p-3" : "p-4"}`}>
      <div className="min-w-0">
        <div className={`font-semibold text-[#373A45] ${compact ? "text-sm" : ""}`}>{title}</div>
        <div className="mt-1 max-w-2xl text-xs text-[#6B7280]">{hint}</div>
        {note && <div className="mt-1 text-xs font-semibold text-[#8a6a1a]" data-testid={`${testid}-note`}>{note}</div>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={title}
        disabled={disabled}
        onClick={onToggle}
        className="shrink-0 text-[#965EA7] disabled:opacity-40"
        data-testid={testid}
      >
        {on ? <ToggleRight size={compact ? 32 : 40} /> : <ToggleLeft size={compact ? 32 : 40} />}
      </button>
    </div>
  );
}

function SaveButton({ onClick, disabled, testid }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#965EA7] px-3 py-1 text-xs text-white hover:bg-[#7A4986] disabled:opacity-60"
      data-testid={testid}>
      <Save size={12} /> Salvează
    </button>
  );
}

function TextSetting({ label, hint, placeholder, stored, onSave, locked, testid }) {
  const [draft, setDraft] = useState(stored || "");
  useEffect(() => { setDraft(stored || ""); }, [stored]);
  const dirty = draft.trim() !== (stored || "");
  return (
    <div className="mt-3 first:mt-0">
      <label htmlFor={testid} className="text-xs font-semibold uppercase tracking-widest text-[#373A45]">{label}</label>
      <textarea
        id={testid}
        value={draft}
        onChange={(e) => setDraft(e.target.value.slice(0, TEXT_MAX))}
        rows={2}
        placeholder={placeholder}
        disabled={locked}
        className="mt-1 w-full rounded-xl border border-[#E5E7EB] bg-[#F5F6F6] px-3 py-2 text-sm text-[#373A45] outline-none focus:ring-2 focus:ring-[#965EA7] disabled:opacity-60"
        data-testid={testid}
      />
      <div className="flex items-start justify-between gap-3 text-[11px] text-[#6B7280]">
        <span>{hint}</span>
        <span className="shrink-0 tabular-nums">{draft.length}/{TEXT_MAX}</span>
      </div>
      {dirty && <SaveButton onClick={() => onSave(draft.trim())} disabled={locked} testid={`${testid}-save`} />}
    </div>
  );
}

function NumberSetting({ label, hint, min, max, stored, onSave, locked, testid }) {
  const [draft, setDraft] = useState(stored ?? "");
  useEffect(() => { setDraft(stored ?? ""); }, [stored]);
  const number = Number(draft);
  const valid = draft !== "" && Number.isInteger(number) && number >= min && number <= max;
  const dirty = String(draft) !== String(stored ?? "");
  return (
    <div>
      <label htmlFor={testid} className="text-xs font-semibold uppercase tracking-widest text-[#373A45]">{label}</label>
      <input
        id={testid}
        type="number" min={min} max={max} step="1"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        disabled={locked}
        className="mt-1 block w-28 rounded-xl border border-[#E5E7EB] bg-[#F5F6F6] px-3 py-2 text-sm tabular-nums text-[#373A45] outline-none focus:ring-2 focus:ring-[#965EA7] disabled:opacity-60"
        data-testid={testid}
      />
      <div className="mt-1 text-[11px] text-[#6B7280]">{hint}</div>
      {dirty && !valid && <div className="mt-1 text-[11px] font-semibold text-[#9b1f2c]">Un număr întreg între {min} și {max}.</div>}
      {dirty && valid && <SaveButton onClick={() => onSave(number)} disabled={locked} testid={`${testid}-save`} />}
    </div>
  );
}
